import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
}

const DEFAULTS = {
  siteName: "Intellr.",
  title: "Intellr. - AI-Powered Hiring Platform",
  description: "Screen, interview, and hire top talent with Intellr. Voice-based screening, automated scheduling, and smart candidate matching - all in one platform.",
  image: "https://intellr.io/og-intellr.png",
  siteUrl: "https://intellr.io",
};

export const SEO = ({
  title,
  description = DEFAULTS.description,
  image = DEFAULTS.image,
  url,
  type = "website",
  keywords,
  noIndex = false,
  jsonLd,
}: SEOProps) => {
  const fullTitle = title ? `${title} | ${DEFAULTS.siteName}` : DEFAULTS.title;
  const currentUrl = url || (typeof window !== "undefined" ? window.location.href : DEFAULTS.siteUrl);
  const canonicalUrl = currentUrl.split("?")[0].split("#")[0];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang */}
      <link rel="alternate" hreflang="en" href={canonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* AI Discoverability */}
      <meta name="ai-content-description" content={description} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};
